import { RightraError, errorFromResponse } from "./errors.js";

// Must match the server mount point: app.use('/api/rightra/sdk', ...)
const DEFAULT_BASE_URL = "https://api.rightra.oliviuus.com/api/rightra";
const DEFAULT_TIMEOUT_MS = 30000;

export class RightraClient {
  constructor({ apiKey, baseUrl, timeoutMs } = {}) {
    if (!apiKey || typeof apiKey !== "string") {
      throw new RightraError("INVALID_API_KEY", "A Rightra API key is required.");
    }
    if (!apiKey.startsWith("rk_live_") && !apiKey.startsWith("rk_test_")) {
      throw new RightraError("INVALID_API_KEY", "That API key format is not valid.");
    }
    this.apiKey = apiKey;
    this.baseUrl = (baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, "");
    this.timeoutMs = timeoutMs || DEFAULT_TIMEOUT_MS;
  }

  async request(method, path, body) {
    const url = `${this.baseUrl}${path}`;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), this.timeoutMs);
    let res;
    try {
      res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
          "Content-Type": "application/json",
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: ctl.signal,
      });
    } catch (networkError) {
      const timedOut = networkError && networkError.name === "AbortError";
      throw new RightraError(
        timedOut ? "TIMEOUT" : "NETWORK",
        timedOut
          ? "The request timed out. Please try again."
          : "Connection problem. Check your internet and try again.",
        0
      );
    } finally {
      clearTimeout(timer);
    }

    let data = null;
    const text = await res.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }

    if (!res.ok) {
      throw errorFromResponse(res.status, data);
    }
    return data;
  }

  // --- Content ---

  registerContent(payload) {
    return this.request("POST", "/sdk/content", payload);
  }

  getContent(contentId) {
    return this.request("GET", `/sdk/content/${encodeURIComponent(contentId)}`);
  }

  uploadKey(contentId, payload) {
    return this.request("POST", `/sdk/content/${encodeURIComponent(contentId)}/key`, payload);
  }

  uploadManifest(contentId, payload) {
    return this.request("POST", `/sdk/content/${encodeURIComponent(contentId)}/manifest`, payload);
  }

  submitFingerprint(contentId, payload) {
    return this.request("POST", `/sdk/content/${encodeURIComponent(contentId)}/fingerprint`, payload);
  }

  // --- Playback ---

  createPlaybackSession(payload) {
    return this.request("POST", "/sdk/playback-session", payload);
  }

  sendPlaybackEvent(payload) {
    return this.request("POST", "/sdk/playback-event", payload);
  }

  sendDownloadAttempt(payload) {
    return this.request("POST", "/sdk/download-attempt", payload);
  }
}